import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'escandallo-menu-del-dia'
const k = (field: string) => `learn.articles.${ARTICLE_ID}.${field}`
const IMG = '/aprender/escandallo-menu-del-dia'

const SOURCES = [
    {
        href: 'https://forbes.es/economia/833253/el-menu-del-dia-se-encarece-un-15-de-media-en-espana-hasta-los-142-euros/',
        labelKey: 'sources.1',
        fallback:
            'Hostelería de España y Edenred, estudio anual del precio del menú del día, 24 de noviembre de 2025 (Forbes España)',
    },
    {
        href: 'https://www.infohoreca.com/noticias/20251125/precio-menu-dia-subida-14-euros-rentabilidad-restaurantes',
        labelKey: 'sources.2',
        fallback: 'InfoHoreca, cobertura del estudio del precio del menú del día, 25 de noviembre de 2025',
    },
    {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        labelKey: 'sources.3',
        fallback: 'Hostelería de España, publicaciones y Anuario del sector',
    },
] as const

export function EscandalloMenuDelDiaArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'canelones', label: t(k('toc.1'), '1. El día que metiste los canelones de marisco') },
        { id: 'modelo', label: t(k('toc.2'), '2. El escandallo del menú del día en cinco pasos') },
        { id: 'iva', label: t(k('toc.3'), '3. ¿Hay que descontar el IVA antes?') },
        { id: 'complementos', label: t(k('toc.4'), '4. Al complemento no se le gana dinero') },
        { id: 'reparto', label: t(k('toc.5'), '5. ¿Cuánto puedes gastarte en cada plato?') },
        { id: 'food-cost', label: t(k('toc.6'), '6. Ese 33 % no es el food cost de tu menú') },
        { id: 'combinaciones', label: t(k('toc.7'), '7. 48 combinaciones y tú solo controlas la media') },
        { id: 'merma', label: t(k('toc.8'), '8. Lo que el escandallo no te va a arreglar') },
        { id: 'esta-semana', label: t(k('toc.9'), '9. Cinco pasos para escandallar tu menú esta semana') },
        {
            id: 'matices',
            label: t(k('toc.10'), '10. El 33 %, el reparto 35/50/15 y lo que aún falta'),
        },
    ]

    const stepRows = [
        { paso: '1', texto: t(k('steps.1'), 'Sumas el coste de los complementos: lo que sí o sí lleva el menú') },
        { paso: '2', texto: t(k('steps.2'), 'Restas ese coste del precio de venta') },
        { paso: '3', texto: t(k('steps.3'), 'Aplicas tu food cost objetivo a lo que queda') },
        {
            paso: '4',
            texto: t(k('steps.4'), 'Repartes ese coste entre primero, segundo y postre según lo que pesa cada uno'),
        },
        {
            paso: '5',
            texto: t(k('steps.5'), 'Con eso ya sabes qué platos entran en el menú y cuáles no'),
        },
    ]

    const plateRows = [
        {
            plato: t(k('plates.1.name'), 'Primero (35 %)'),
            coste: t(k('plates.1.cost'), '1,51 €'),
        },
        {
            plato: t(k('plates.2.name'), 'Principal (50 %)'),
            coste: t(k('plates.2.cost'), '2,15 €'),
        },
        {
            plato: t(k('plates.3.name'), 'Postre (15 %)'),
            coste: t(k('plates.3.cost'), '0,65 €'),
        },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Escandalla los complementos primero. Pan, bebida, café. Es media hora y es el número que arrastra todo lo demás.',
        ),
        t(k('week.2'), 'Divide tu precio entre 1,10 y trabaja siempre con esa cifra.'),
        t(
            k('week.3'),
            'Fija tu reparto entre primero, principal y postre. Si no tienes uno propio, empieza por 35 / 50 / 15 y ajústalo.',
        ),
        t(
            k('week.4'),
            'Saca el coste máximo de cada categoría y pásale ese filtro a los platos que ya tienes en la pizarra.',
        ),
        t(
            k('week.5'),
            'Pide a cocina que apunte una semana qué combinación pide cada mesa. Sin eso, estás escandallando a ciegas.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Un menú del día de 16 € solo te deja 4,30 € para pagar primero, segundo y postre. Cómo hacer bien el escandallo y qué platos puedes meter en la pizarra.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Pizarra de menú del día en un restaurante, con platos del mediodía en primer plano',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('lead'),
                                        'Un menú del día de 16 € solo te deja 4,30 € para pagar primero, segundo y postre.',
                                    )}
                                </span>{' '}
                                {t(
                                    k('intro.1'),
                                    'El precio medio en España está en 14,2 €, según el estudio de Hostelería de España y Edenred publicado el 24 de noviembre de 2025. Y el escandallo de un menú no se hace como el de un plato de carta: hay complementos que no dan margen, un IVA del 10 % que casi nadie descuenta antes de calcular y un reparto entre platos que es el que de verdad decide qué sube a la pizarra.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(k('intro.2Before'), 'Si vienes de ')}
                                <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                    {t(k('intro.2Link'), 'cómo hacer un escandallo paso a paso')}
                                </LearnArticleLink>
                                {t(
                                    k('intro.2After'),
                                    ', aquí el salto es otro: aplicar esa lógica al menú completo, no a un plato suelto.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='canelones' className={articleUi.h2}>
                        {t(k('h2.canelones'), 'El día que metiste los canelones de marisco')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.canelones.1'), 'Tenías género y quedaban bien en la pizarra.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.canelones.2'),
                                'Los metiste de primero, con la ensalada mixta al lado, pensando que entre los dos se compensaba.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.canelones.3'),
                                'Ese mediodía entraron ochenta personas y cuarenta pidieron canelones. Nadie hizo nada mal. Simplemente el cliente eligió, que es lo que hace siempre.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.canelones.4'), 'A final de mes miras la cuenta y no cuadra. Y no cuadra por eso.')}
                        </p>
                    </div>

                    <h2 id='modelo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.modelo'), 'El escandallo del menú del día en cinco pasos')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.modelo.1'),
                                'No hay un modelo único. Este es el que usamos nosotros, en cinco pasos.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('steps.caption'), 'Cinco pasos del escandallo del menú del día')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('steps.col.paso'), 'Paso')}</th>
                                    <th scope='col'>{t(k('steps.col.que'), 'Qué haces')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stepRows.map(row => (
                                    <tr key={row.paso}>
                                        <th scope='row'>{row.paso}</th>
                                        <td>{row.texto}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.modelo.2'),
                                'La diferencia con el escandallo de carta está en los pasos 1 y 4. El resto es lo de siempre.',
                            )}
                        </p>
                    </div>

                    <h2 id='iva' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.iva'), '¿Hay que descontar el IVA antes de escandallar el menú?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.iva.1'), 'Sí. Y este es el paso que más veces se salta.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.iva.2Em'), 'Un menú de 16 € no son 16 € tuyos: son 14,55 €.')}
                            </span>{' '}
                            {t(
                                k('p.iva.2'),
                                'Los otros 1,45 € son el 10 % de IVA, y ese dinero es de Hacienda desde el minuto uno.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.iva.3'),
                                'Si haces el escandallo sobre el precio con IVA dentro, te crees que puedes gastarte 4,79 € en los tres platos. La cifra real es 4,30 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.iva.4Before'), 'Son 48 céntimos por menú. ')}
                            <em>
                                {t(k('p.iva.4Em'), 'A 1.000 menús al mes, unos 480 € que creías tener y no tenías.')}
                            </em>
                        </p>
                    </div>

                    <h2 id='complementos' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.complementos'), 'Al complemento no se le gana dinero, se le cubre el coste')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.complementos.1'), 'Pan, bebida y café. En algunos sitios también el aperitivo.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.complementos.2'),
                                'Van dentro del precio. El cliente no elige si los quiere y tú no eliges si se los pones.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.complementos.3Em'),
                                    'Nuestra regla: al complemento no se le gana margen, solo se recupera el coste.',
                                )}
                            </span>{' '}
                            {t(
                                k('p.complementos.3'),
                                'Por eso el coste de los complementos se resta de la base imponible antes de calcular nada más.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.complementos.4'),
                                'Con un coste de complementos de 1,50 € sobre esos 14,55 €, te quedan 13,05 € antes de aplicar el food cost objetivo.',
                            )}
                        </p>
                    </div>

                    <aside
                        className={articleUi.breakout}
                        aria-label={t(k('breakout.aria'), 'Escandallo visual del menú del día')}
                    >
                        <figure className='m-0 mx-auto w-full max-w-[min(100%,380px)]'>
                            <img
                                src={`${IMG}/infografia.webp`}
                                alt={t(
                                    k('infografiaAlt'),
                                    'Infografía: escandallo de un menú del día de 16 € hasta los 4,30 € disponibles para los tres platos',
                                )}
                                width={800}
                                height={1120}
                                className='block h-auto w-full rounded-article-frame'
                                loading='lazy'
                                decoding='async'
                            />
                        </figure>
                        <div className={articleUi.breakCopy}>
                            <p className={articleUi.breakEyebrow}>{t(k('breakout.eyebrow'), 'De 16 € a 4,30 €')}</p>
                            <p className={articleUi.breakLead}>
                                {t(k('breakout.lead'), 'Lo que de verdad te queda para poner en el plato.')}
                            </p>
                            <p className={cn(articleUi.p, 'mb-0')}>
                                {t(
                                    k('breakout.p'),
                                    'Primero se va el IVA (1,45 €). Luego pan, bebida y café a coste (1,50 €). Del resto, personal, alquiler, luz y tu beneficio se llevan 8,74 €. Para primero, principal y postre quedan 4,30 €, y el food cost real del menú, con complementos incluidos, corre al 39,9 %.',
                                )}
                            </p>
                        </div>
                    </aside>

                    <h2 id='reparto' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.reparto'), '¿Cuánto puedo gastarme en cada plato del menú?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.reparto.1'),
                                'No es lo mismo dar una ensalada de primero que unos canelones de marisco. Repartir el coste a partes iguales entre los tres platos es la forma más rápida de que el segundo se te coma el menú entero.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.reparto.2Before'), 'Nosotros partimos de este reparto. ')}
                            <em>
                                {t(
                                    k('p.reparto.2Em'),
                                    'Es nuestro punto de partida, no un estándar del sector: cada casa tiene que ajustarlo a su carta.',
                                )}
                            </em>
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('plates.caption'), 'Coste máximo por plato con un menú de 16 €')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('plates.col.plato'), 'Plato')}</th>
                                    <th scope='col'>{t(k('plates.col.coste'), 'Coste máximo con un menú de 16 €')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plateRows.map(row => (
                                    <tr key={row.plato}>
                                        <th scope='row'>{row.plato}</th>
                                        <td>
                                            <strong>{row.coste}</strong>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.reparto.3'),
                                'Ese es el filtro. Un plato que se pase de ahí no entra, o entra sabiendo lo que cuesta que entre.',
                            )}
                        </p>
                    </div>

                    <h2 id='food-cost' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.foodCost'), 'Ese 33 % no es el food cost de tu menú')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.foodCost.1'),
                                'Si aplicas un 33 % de food cost objetivo a los 13,05 € que quedan tras descontar complementos, el coste de los tres platos te sale en 4,30 €.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.foodCost.2'), 'Pero al menú completo hay que sumarle otra vez los complementos.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('p.foodCost.3Em'),
                                    'Coste total del menú: 5,80 € sobre 14,55 € de base. Un 39,9 %.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.foodCost.4'),
                                'No es un error de cálculo. Es la consecuencia de la decisión anterior: los complementos entran a coste y no aportan margen. Si tú creías que tu menú corre al 33 %, corre al 40 %.',
                            )}
                        </p>
                    </div>

                    <h2 id='combinaciones' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.combinaciones'), '48 combinaciones y tú solo controlas la media')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.combinaciones.1Before'), 'Cuatro primeros, cuatro segundos y tres postres son ')}
                            <strong>{t(k('p.combinaciones.1Strong'), '48 combinaciones posibles')}</strong>
                            {t(k('p.combinaciones.1After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.combinaciones.2'), 'El cliente elige una. Tú no.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.combinaciones.3Before'),
                                'Va un caso con números de un escandallo real: primero 1,48 €, principal 3,38 €, postre 1,07 €. Total, ',
                            )}
                            <strong>{t(k('p.combinaciones.3Strong'), '5,93 €')}</strong>
                            {t(k('p.combinaciones.3After'), ' en los tres platos.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.combinaciones.4'),
                                'En un menú de 23 € esa combinación cabe de sobra: el presupuesto es de 6,40 € y te sobran 47 céntimos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.combinaciones.5Before'), 'En un menú de 16 € el presupuesto es de 4,30 € y ')}
                            <strong>{t(k('p.combinaciones.5Strong'), 'te pasas en 1,62 €')}</strong>
                            {t(k('p.combinaciones.5Mid'), '. El food cost real de ese cliente se te va al ')}
                            <strong>{t(k('p.combinaciones.5Strong2'), '51 %')}</strong>
                            {t(k('p.combinaciones.5After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <em>
                                {t(
                                    k('p.combinaciones.6Em'),
                                    'A 1.000 menús al mes, si esa fuera la combinación dominante, son 1.620 € de margen que desaparecen.',
                                )}
                            </em>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.combinaciones.7'),
                                'En esa combinación el reparto real del coste fue 25 % primero, 57 % principal, 18 % postre, cuando el plan era 35 / 50 / 15. El principal se comió siete puntos que no eran suyos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.combinaciones.8'),
                                'Por eso el promedio de tu menú puede estar perfecto y aun así perder dinero todos los días.',
                            )}
                        </p>
                    </div>

                    <h2 id='merma' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.merma'), 'Lo que el escandallo no te va a arreglar: la merma')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.1'),
                                'El menú del día funciona por volumen. Volumen significa márgenes más justos, y márgenes justos significan que la merma pesa el doble que en la carta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.2'),
                                'Aquí no hay hoja de cálculo que valga. El secreto es liquidarlo todo, y eso es control de la demanda y oficio del jefe de cocina.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.merma.3'),
                                'El escandallo te dice qué puedes meter. La merma decide cuánto te llevas.',
                            )}
                        </p>
                    </div>

                    <h2 id='esta-semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Cinco pasos para escandallar tu menú esta semana')}
                    </h2>
                    <ol className={articleUi.numListGap2xl}>
                        {weekItems.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'El 33 %, el reparto 35/50/15 y lo que aún falta')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'El 33 % de food cost no es una ley. Es un punto de partida cómodo, y en menú del día hay casas que trabajan bien al 38 % porque el volumen lo sostiene.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'El reparto 35 / 50 / 15 es criterio nuestro, no un estándar publicado. Si tu carta es de guisos, probablemente el primero te pese más.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.3'),
                                'Y queda lo más difícil, que es el paso 6 que todavía no existe: saber de antemano qué combinaciones concretas te rompen el margen y cómo arreglarlas sin tocar el precio. En Tipi estamos trabajando justo en eso. Si te interesa probarlo antes que nadie y contarnos qué te falta, escríbenos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.4'),
                                'Si quieres saber si de verdad tu menú te da dinero, no pierdas más el tiempo: escríbenos y le ponemos números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('closing.signoff'), 'Nos vemos en la próxima, que de estas caen varias al mes.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Quieres saber si tu menú del día de verdad te da dinero?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Escandallamos contigo los complementos, el IVA y el filtro por plato. Escríbenos y le ponemos números.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={articleUi.link}
                                >
                                    {t(k(source.labelKey), source.fallback)}
                                </a>
                            </li>
                        ))}
                        <li>
                            {t(
                                k('sources.4'),
                                'Escandallo de ejemplo y modelo de reparto: elaboración propia de Tipi.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
